import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Briefcase, BookOpen, TrendingUp, ArrowLeft, ExternalLink } from 'lucide-react';
import { useDataService } from '../context/DataContext';
import useResponsive from '../hooks/useResponsive';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const About = () => {
  const [profileData, setProfileData] = useState(null);
  const [publications, setPublications] = useState([]);
  const [careerData, setCareerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isMobile } = useResponsive();
  const dataService = useDataService();

  useEffect(() => {
    const loadAllData = async () => {
      try {
        setLoading(true);
        
        // Load profile
        const profile = await dataService.getProfile();
        setProfileData(profile);
        
        // Load publications from API
        try {
          const publicationsResponse = await dataService.getPublications();
          setPublications(publicationsResponse);
        } catch (err) {
          console.error('Error loading publications:', err);
          // Fallback to empty array
          setPublications([]);
        }
        
        // Load career experience from API
        try {
          const careerResponse = await dataService.getCareerExperience();
          const careerList = Array.isArray(careerResponse) 
            ? careerResponse 
            : (careerResponse.experiences || []);
          // Sort by order field (ascending - lower order numbers first)
          const sortedCareer = [...careerList].sort((a, b) => {
            const orderA = a.order ?? 999;
            const orderB = b.order ?? 999;
            return orderA - orderB;
          });
          setCareerData(sortedCareer);
        } catch (err) {
          console.error('Error loading career experience:', err);
          setCareerData([]);
        }
      } catch (err) {
        console.error('Error loading profile:', err);
        // Fallback to mock data
        try {
          const { profileData: mockProfile } = await import('../data/mock');
          setProfileData(mockProfile);
        } catch (mockErr) {
          console.error('Error loading mock profile:', mockErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [dataService]);

  const expertise = [
    {
      icon: <TrendingUp size={20} />,
      title: "Market Analysis",
      description: "Deep-dive research into emerging markets, competitive landscapes, and industry trends."
    },
    {
      icon: <Briefcase size={20} />,
      title: "Investment Strategy", 
      description: "Developing investment thesis and due diligence frameworks for startup ecosystems."
    },
    {
      icon: <BookOpen size={20} />,
      title: "Research & Writing",
      description: "Publishing insights on startup patterns, funding trends, and ecosystem development."
    }
  ];



  if (loading) {
    return (
      <div className="min-h-screen bg-vintage-cream flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (!profileData) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load profile</h2>
        <Link 
          to="/"
          className="text-gray-900 hover:text-gray-700 font-medium transition-colors"
        >
          Back
        </Link>
      </div>
    );
  }

  // Get initials for profile picture
  const getInitials = (name) => {
    if (!name) return 'JV';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  return (
    <div className="min-h-screen bg-vintage-cream">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation */}
        <nav className="mb-8">
          <Link 
            to="/" 
            className="text-vintage-faded hover:text-vintage-accent flex items-center space-x-2 transition-colors"
          >
            <ArrowLeft size={16} />
            <span>Back</span>
          </Link>
        </nav>

        {/* Hero Section */}
        <section className="mb-16">
          <div className="vintage-card p-8 md:p-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
              <div className="md:col-span-2">
                <h1 className="vintage-heading-1 text-3xl md:text-4xl text-vintage-ink mb-4">
                  About {profileData.name}
                </h1>
                <p className="vintage-heading-2 text-xl text-vintage-faded mb-6">
                  {profileData.title}
                </p>
                <div className="vintage-body text-vintage-faded space-y-4">
                  <p>
                    I specialize in researching startup ecosystems and analyzing investment opportunities 
                    across emerging markets. My work focuses on understanding the patterns that drive 
                    successful technology companies and the market dynamics that create sustainable 
                    competitive advantages.
                  </p>
                  <p>
                    Through detailed market analysis and investment memos, I help investors and 
                    entrepreneurs navigate the complexities of building and scaling technology companies 
                    in diverse market conditions.
                  </p>
                  <p>
                    My research combines quantitative analysis with qualitative insights to provide 
                    actionable intelligence for investment decisions and strategic planning.
                  </p>
                </div>
              </div>
              <div className="text-center">
                {/* Profile Picture */}
                <div className="mb-6 flex justify-center">
                  <div className="relative group">
                    <div className="w-32 h-32 bg-vintage-ink rounded-full p-1 shadow-xl transition-all group-hover:shadow-2xl">
                      <div className="w-full h-full bg-vintage-cream rounded-full flex items-center justify-center relative overflow-hidden border-2 border-vintage-accent">
                        {profileData.avatar_url ? (
                          <img 
                            src={profileData.avatar_url} 
                            alt={profileData.name}
                            className="w-full h-full object-cover rounded-full"
                          />
                        ) : (
                          <span className="vintage-heading-1 text-vintage-ink font-bold text-4xl">
                            {getInitials(profileData.name)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 vintage-caption text-vintage-faded">
                  {profileData.location && (
                    <div className="flex items-center justify-center space-x-2">
                      <MapPin size={16} />
                      <span>{profileData.location}</span>
                    </div>
                  )}
                  <div className="flex items-center justify-center space-x-2">
                    <Mail size={16} />
                    <Link to="/contact" className="hover:text-vintage-accent transition-colors">
                      Get in touch
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Expertise */}
        <section className="mb-16">
          <h2 className="vintage-heading-3 text-vintage-ink mb-8">Areas of Expertise</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {expertise.map((area, index) => (
              <div key={index} className="vintage-card p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="text-vintage-accent">
                    {area.icon}
                  </div>
                  <h3 className="vintage-heading-2 text-vintage-ink">{area.title}</h3>
                </div>
                <p className="vintage-body text-vintage-faded leading-relaxed">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </section>

        {/* Professional Experience */}
        {careerData.length > 0 && (
          <section className="mb-16">
            <h2 className="vintage-heading-3 text-vintage-ink mb-8">Professional Experience</h2>
            <div className="space-y-6">
              {careerData.map((experience, index) => (
                <div key={experience.id} className="vintage-card p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                    <div className="flex items-start space-x-4 flex-1">
                      <div className="w-12 h-12 bg-vintage-accent rounded-lg flex items-center justify-center flex-shrink-0">
                        <Briefcase className="text-vintage-cream" size={20} />
                      </div>
                      <div className="flex-1">
                        <h3 className="vintage-heading-2 text-xl text-vintage-ink mb-1">
                          {experience.role}
                        </h3>
                        <p className="typewriter text-vintage-accent font-medium">
                          {experience.company}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-start md:items-end gap-2 mt-2 md:mt-0 md:ml-4">
                      <span className="vintage-caption text-vintage-faded">
                        {experience.period}
                      </span>
                      {experience.location && (
                        <span className="vintage-caption text-vintage-faded flex items-center space-x-1">
                          <MapPin size={14} />
                          <span>{experience.location}</span>
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {experience.description && (
                    <p className="vintage-body text-vintage-faded leading-relaxed mb-4">
                      {experience.description}
                    </p>
                  )}
                  
                  {experience.achievements && experience.achievements.length > 0 && (
                    <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--vintage-accent)' }}>
                      <h4 className="vintage-caption text-vintage-ink font-medium mb-3 typewriter">Key Achievements:</h4>
                      <ul className="space-y-2">
                        {experience.achievements.map((achievement, idx) => (
                          <li key={idx} className="vintage-body text-vintage-faded flex items-start">
                            <span className="text-vintage-accent mr-3 mt-1 flex-shrink-0">•</span>
                            <span>{achievement}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Publications */}
        <section className="mb-16">
          <h2 className="vintage-heading-3 text-vintage-ink mb-8">Research & Publications</h2>
          <div className="space-y-6">
            {publications.length > 0 ? (
              publications.filter(pub => pub.featured !== false).map((pub) => (
                <div key={pub.id} className="vintage-card p-6 hover:shadow-lg transition-shadow">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-2">
                        <h3 className="vintage-heading-2 text-xl text-vintage-ink mb-2 md:mb-0">{pub.title}</h3>
                        {pub.url && (
                          <a 
                            href={pub.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="vintage-link text-vintage-accent hover:text-vintage-ink inline-flex items-center space-x-1"
                          >
                            <span className="text-sm">View</span>
                            <ExternalLink size={14} />
                          </a>
                        )}
                      </div>
                      {pub.description && (
                        <p className="vintage-body text-vintage-faded leading-relaxed mb-3">
                          {pub.description}
                        </p>
                      )}
                      <div className="space-y-1">
                        {pub.type && (
                          <p className="vintage-caption text-vintage-faded">
                            <span className="typewriter font-medium">Type:</span> {pub.type}
                          </p>
                        )}
                        {pub.category && (
                          <p className="vintage-caption text-vintage-faded">
                            <span className="typewriter font-medium">Category:</span> {pub.category}
                          </p>
                        )}
                        {pub.publisher && (
                          <p className="vintage-caption text-vintage-faded">
                            <span className="typewriter font-medium">Platform:</span> {pub.publisher}
                          </p>
                        )}
                        {pub.doi && (
                          <p className="vintage-caption text-vintage-faded">
                            <span className="typewriter font-medium">DOI:</span> {pub.doi}
                          </p>
                        )}
                      </div>
                      {pub.tags && pub.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {pub.tags.map((tag, idx) => (
                            <span key={idx} className="vintage-caption text-vintage-faded">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="vintage-body text-vintage-faded">No publications available.</p>
            )}
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mb-16">
          <div className="vintage-card p-8 text-center">
            <h2 className="vintage-heading-2 text-2xl text-vintage-ink mb-4">Let's Connect</h2>
            <p className="vintage-body text-vintage-faded mb-6 max-w-2xl mx-auto">
              I'm always interested in discussing startup ecosystems, investment opportunities, 
              and market trends. Feel free to reach out if you'd like to collaborate or exchange insights.
            </p>
            <Link 
              to="/contact"
              className="vintage-btn vintage-btn-accent inline-flex items-center space-x-2"
            >
              <Mail size={18} />
              <span>Get In Touch</span>
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

export default About;
